import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, Lock } from "lucide-react";
import {
  counterpartStage,
  missingPrep,
} from "@/platform/playbooks/counterpart";
import { getPlaybook } from "@/platform/playbooks/registry";
import { LOOP_STAGES, type LoopStage } from "@/platform/playbooks/types";
import { go } from "@/shared/dashboard/shell/navigate";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { playbookCopy } from "@/shared/i18n/playbooks";
import { counterpartsCopy } from "./copy";
import { LoopLine } from "./LoopLine";
import { useCounterpart, type Counterpart } from "./useCounterparts";
import "./counterparts.css";

function counterpartIdFromPath(pathname: string): string {
  return decodeURIComponent(pathname.match(/^\/counterparts\/([^/]+)/)?.[1] ?? "");
}

function samePrep(a: Record<string, string>, b: Record<string, string>) {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) if ((a[key] ?? "").trim() !== (b[key] ?? "").trim()) return false;
  return true;
}

function PrepForm({
  counterpart,
  onSave,
}: {
  counterpart: Counterpart;
  onSave: (prep: Record<string, string>) => Promise<unknown>;
}) {
  const { locale } = useLanguage();
  const copy = counterpartsCopy(locale);
  const pb = playbookCopy(locale);
  const playbook = getPlaybook(counterpart.file.playbook);
  const [draft, setDraft] = useState<Record<string, string>>(
    counterpart.file.prep,
  );
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">(
    "idle",
  );
  const dirty = !samePrep(draft, counterpart.file.prep);
  const missing = missingPrep(playbook, draft);

  // Leaving with unsaved notes loses intel the learner may not have elsewhere.
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const save = async () => {
    setStatus("saving");
    try {
      await onSave(draft);
      setStatus("saved");
    } catch {
      setStatus("error");
    }
  };

  return (
    <form
      className="eq-counterpart-prep"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      {playbook.prep.map((field) => {
        const id = `prep-${field.id}`;
        return (
          <div key={field.id} className="eq-prep-field">
            <label htmlFor={id} className="eq-prep-field-head">
              <strong>{pb(field.label)}</strong>
              <span className={field.required ? "is-required" : undefined}>
                {field.required ? copy.required : copy.optional}
              </span>
            </label>
            <p id={`${id}-hint`}>{pb(field.hint)}</p>
            <textarea
              id={id}
              className="eq-input"
              rows={field.maxLength > 4000 ? 8 : 4}
              maxLength={field.maxLength}
              aria-describedby={`${id}-hint`}
              aria-required={field.required}
              value={draft[field.id] ?? ""}
              onChange={(event) => {
                setDraft((current) => ({
                  ...current,
                  [field.id]: event.target.value,
                }));
                if (status !== "saving") setStatus("idle");
              }}
            />
          </div>
        );
      })}
      <div className="eq-prep-footer">
        <p role="status" aria-live="polite">
          {status === "error"
            ? copy.saveError
            : status === "saving"
              ? copy.saving
              : dirty
                ? copy.unsaved
                : missing.length > 0
                  ? `${copy.stillNeeded} ${missing.map((field) => pb(field.label)).join(", ")}`
                  : status === "saved"
                    ? `${copy.saved}. ${copy.prepDone}`
                    : copy.prepDone}
        </p>
        <button
          type="submit"
          className="eq-button eq-button--primary"
          disabled={!dirty || status === "saving"}
        >
          {status === "saving" ? copy.saving : copy.save}
        </button>
      </div>
    </form>
  );
}

/** A later stage of the loop, described but not yet actionable. */
function UpcomingStage({ stage, index }: { stage: LoopStage; index: number }) {
  const { locale } = useLanguage();
  const copy = counterpartsCopy(locale);
  return (
    <section className="eq-surface eq-panel eq-loop-stage is-upcoming" aria-labelledby={`stage-${stage}`}>
      <header>
        <span className="eq-loop-explainer-number" aria-hidden="true">{index + 1}</span>
        <h2 id={`stage-${stage}`}>{copy.stages[stage]}</h2>
        <span className="eq-loop-stage-lock">
          <Lock size={13} aria-hidden="true" />
          {copy.notYet}
        </span>
      </header>
      <p>{copy.stageBodies[stage]}</p>
    </section>
  );
}

export function CounterpartPage() {
  const { locale } = useLanguage();
  const copy = counterpartsCopy(locale);
  const pb = playbookCopy(locale);
  const id = useMemo(() => counterpartIdFromPath(window.location.pathname), []);
  const { counterpart, state, retry, savePrep } = useCounterpart(id);

  const back = (
    <a
      href="/counterparts"
      className="eq-counterpart-back"
      onClick={(event) => {
        event.preventDefault();
        go("/counterparts");
      }}
    >
      <ArrowLeft size={16} aria-hidden="true" />
      {copy.back}
    </a>
  );

  if (state === "loading")
    return (
      <div className="eq-tools-page eq-counterparts-page">
        {back}
        <p role="status">{copy.loading}</p>
      </div>
    );
  if (state !== "ready" || !counterpart)
    return (
      <div className="eq-tools-page eq-counterparts-page">
        {back}
        <div className="eq-surface eq-panel" role="alert">
          <p>{state === "error" ? copy.loadError : copy.notFound}</p>
          {state === "error" && (
            <button className="eq-button" onClick={retry}>
              {copy.retry}
            </button>
          )}
        </div>
      </div>
    );

  const playbook = getPlaybook(counterpart.file.playbook);
  const stage = counterpartStage(counterpart.file);

  return (
    <div className="eq-tools-page eq-counterparts-page">
      {back}
      <header className="eq-counterpart-head">
        <div className="eq-eyebrow">
          {pb(playbook.labels.name)} · {pb(playbook.labels.counterpart)}
        </div>
        <h1>{counterpart.name}</h1>
        <div className="eq-counterpart-head-line">
          <LoopLine stage={stage} labelled />
          <p>
            <strong>
              {copy.next}: {copy.stages[stage]}
            </strong>{" "}
            — {copy.stageBodies[stage]}
          </p>
        </div>
      </header>

      {/* Finished prep folds away so the next step sits right under the
          header; it stays one click from editing. */}
      <details
        className="eq-surface eq-panel eq-loop-stage eq-prep-stage"
        open={stage === "prep" || undefined}
      >
        <summary>
          <span className="eq-loop-explainer-number" aria-hidden="true">
            {stage === "prep" ? "1" : <Check size={12} strokeWidth={3} />}
          </span>
          <h2 id="stage-prep">{copy.stages.prep}</h2>
          {stage !== "prep" && (
            <span className="eq-prep-stage-done">
              {copy.complete}
              <span className="eq-prep-stage-edit">{copy.edit}</span>
            </span>
          )}
        </summary>
        <PrepForm
          key={counterpart.id}
          counterpart={counterpart}
          onSave={savePrep}
        />
      </details>
      {LOOP_STAGES.slice(1).map((item, index) => (
        <UpcomingStage key={item} stage={item} index={index + 1} />
      ))}
    </div>
  );
}
